import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Button, estiloDeBoton } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Select } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  ESTADOS, useAdjuntarRespuesta, useCambiarEstado, useSolicitud,
  type EstadoDeSolicitud,
} from './api';
import { InsigniaDeEstado } from './piezas';

/**
 * DetalleDeSolicitud — un radicado: su PDF, su historial y, para gestión, la
 * decisión y el documento de respuesta.
 *
 * ## Lo que cambia
 *
 * - **La nota del cambio de estado se escribe en un campo**, no en un
 *   `prompt()` del navegador. (Ese `prompt` ya se retiró del vanilla el
 *   2026-09-24, al ampliar la regla de `test_shell.py`.)
 * - **El nombre de quien radicó y la nota de respuesta se escapan.** Eran dos
 *   de las nueve interpolaciones crudas del módulo, y las dos cruzan un límite
 *   de privilegio: `user_name` lo escribe Administración y `response_note` lo
 *   escribe Bienestar, y las lee el colaborador.
 * - **Adjuntar la respuesta usa `API.upload`**, que lleva el token; el vanilla
 *   armaba un `fetch` a mano con `localStorage` dentro.
 * - **Las fechas del historial se ven en la hora de quien mira**: eran
 *   `created_at.replace('T',' ').slice(0,16)`, o sea UTC en crudo.
 */
export function DetalleDeSolicitud({
  id, esGestion, onCerrar,
}: {
  id: number;
  esGestion: boolean;
  onCerrar: () => void;
}) {
  const consulta = useSolicitud(id);
  const cambiar = useCambiarEstado();
  const adjuntar = useAdjuntarRespuesta();

  const [nota, setNota] = useState('');
  const [estado, setEstado] = useState<EstadoDeSolicitud>('en_revision');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [notaRespuesta, setNotaRespuesta] = useState('');
  const [error, setError] = useState('');

  const d = consulta.data;

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v && !cambiar.isPending && !adjuntar.isPending) onCerrar(); }}
      titulo={d ? d.radicado : 'Solicitud'}
      descripcion={d?.template_name}
      ancho="md"
      pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
    >
      {consulta.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : consulta.isError ? (
        <EstadoError err={consulta.error} que="la solicitud" onReintentar={() => void consulta.refetch()} />
      ) : !d ? null : (
        <div className="flex flex-col gap-4">
          <p className="flex flex-wrap items-center gap-2">
            <InsigniaDeEstado solicitud={d} />
            {esGestion && d.user_name && (
              <span className="text-2 text-muted">
                <i className="bi bi-person" aria-hidden="true" /> {d.user_name} · {d.area}
              </span>
            )}
          </p>

          <div className="flex flex-wrap gap-2">
            {/* El enlace lo firma el servidor y caduca; si ya no sirve, el
                navegador lo dice al abrirlo, que es más honesto que esconderlo. */}
            {d.pdf_url && (
              <a
                href={d.pdf_url}
                target="_blank"
                rel="noopener"
                className={cn(estiloDeBoton({ variant: 'secondary', size: 'sm' }), 'no-underline')}
              >
                <i className="bi bi-file-earmark-pdf" aria-hidden="true" /> Ver el documento
              </a>
            )}
            {d.response_url && (
              <a
                href={d.response_url}
                target="_blank"
                rel="noopener"
                className={cn(estiloDeBoton({ size: 'sm' }), 'no-underline')}
              >
                <i className="bi bi-paperclip" aria-hidden="true" /> Documento de respuesta
              </a>
            )}
          </div>

          {d.response_note && (
            <p className="rounded-sm border border-info/40 bg-info/12 p-3 text-2 text-ink-2">
              <strong>Nota de Bienestar:</strong> {d.response_note}
            </p>
          )}

          <section>
            <h3 className="mb-1.5 flex items-center gap-2 text-3 font-bold text-brand-ink">
              <i className="bi bi-clock-history" aria-hidden="true" /> Historial
            </h3>
            {d.timeline.length ? (
              <ol className="flex flex-col gap-1.5">
                {d.timeline.map((e, i) => (
                  <li key={i} className="flex items-baseline gap-2 text-2">
                    <i className="bi bi-dot shrink-0 text-brand-ink" aria-hidden="true" />
                    <span className="min-w-0">
                      <strong className="text-ink">{e.actor}</strong> · {e.note || e.action}
                      <span className="block text-1 text-muted">
                        {e.created_at ? Utils.fmtDatetime(e.created_at) : '—'}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-2 text-muted">Sin movimientos todavía.</p>
            )}
          </section>

          {esGestion && (
            <>
              <hr className="border-line" />

              <section className="flex flex-col gap-3">
                <h3 className="text-3 font-bold text-brand-ink">Cambiar el estado</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Estado">
                    <Select
                      value={estado}
                      onChange={(e) => setEstado(e.target.value as EstadoDeSolicitud)}
                    >
                      {ESTADOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </Select>
                  </Field>
                  <Field label="Nota" opcional ayuda="La ve quien radicó la solicitud.">
                    <Input
                      value={nota}
                      onChange={(e) => setNota(e.target.value)}
                      placeholder="Por qué cambia el estado"
                    />
                  </Field>
                </div>
                <div>
                  <Button
                    size="sm"
                    loading={cambiar.isPending}
                    onClick={() => cambiar.mutate(
                      { id, status: estado, note: nota.trim() || null },
                      {
                        onSuccess: () => { Utils.toast('Estado actualizado', 'success'); setNota(''); },
                        onError: (err) => Utils.toast(
                          err instanceof Error ? err.message : 'No se pudo actualizar', 'error'),
                      },
                    )}
                  >
                    <i className="bi bi-check2-circle" aria-hidden="true" /> Guardar el estado
                  </Button>
                </div>
              </section>

              <section className="flex flex-col gap-3">
                <h3 className="text-3 font-bold text-brand-ink">Adjuntar la respuesta</h3>
                <Field
                  label="Documento (PDF)"
                  ayuda="Al adjuntarlo, la solicitud pasa a «Respondida»."
                  error={error || undefined}
                >
                  <input
                    type="file"
                    accept="application/pdf"
                    className="block w-full text-3 text-ink-2 file:mr-3 file:rounded-sm file:border-0 file:bg-brand file:px-3 file:py-2 file:text-3 file:font-semibold file:text-on-brand hover:file:bg-brand-strong"
                    onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); setError(''); }}
                  />
                </Field>
                <Field label="Nota de la respuesta" opcional>
                  <Input
                    value={notaRespuesta}
                    onChange={(e) => setNotaRespuesta(e.target.value)}
                  />
                </Field>
                <div>
                  <Button
                    size="sm"
                    loading={adjuntar.isPending}
                    disabled={!archivo}
                    razonDeshabilitado="Elige primero el PDF"
                    onClick={() => {
                      if (!archivo) return;
                      adjuntar.mutate(
                        { id, archivo, nota: notaRespuesta.trim() },
                        {
                          onSuccess: () => {
                            Utils.toast('Respuesta adjuntada', 'success');
                            setArchivo(null);
                            setNotaRespuesta('');
                          },
                          onError: (err) => setError(
                            err instanceof Error ? err.message : 'No se pudo adjuntar'),
                        },
                      );
                    }}
                  >
                    <i className="bi bi-upload" aria-hidden="true" /> Adjuntar
                  </Button>
                </div>
              </section>
            </>
          )}
        </div>
      )}
    </Drawer>
  );
}
